import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate, useParams } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { ArrowLeft, CheckCircle2, CreditCard } from 'lucide-react';

/**
 * 테스트보철 상세 (/test-cases/:id)
 * - 공개: 케이스 내용 (누구나)
 * - 비공개: 치과/기공소 정보 (당사자만, 매칭 후 상호 공개)
 * - 기공소: 수락 (월 1회, 최초 무료, 이후 1만원 이용권)
 * - 상태 전이: 기공소(제작중/납품), 치과(완료 확정/취소)
 */
const STATUS_LABEL = {
    open: { text: '모집중', color: '#059669', bg: '#ECFDF5' },
    accepted: { text: '매칭완료', color: '#2563EB', bg: '#EFF6FF' },
    in_progress: { text: '제작중', color: '#D97706', bg: '#FFFBEB' },
    delivered: { text: '납품완료', color: '#7C3AED', bg: '#F5F3FF' },
    completed: { text: '완료', color: '#6B7280', bg: '#F3F4F6' },
    cancelled: { text: '취소됨', color: '#DC2626', bg: '#FEF2F2' },
};

const btnStyle = (bg) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color: '#fff',
    border: 'none', padding: '12px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer',
});

function TestCaseDetail({ user }) {
    const navigate = useNavigate();
    const { requestId } = useParams();
    const [request, setRequest] = useState(null);
    const [privateInfo, setPrivateInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);
    const [showPassModal, setShowPassModal] = useState(false);

    const uid = auth.currentUser?.uid;
    const businessType = user?.businessType || user?.userType;
    const isLab = businessType === 'lab' || businessType === 'outsource';
    const isDental = businessType === 'dental' || businessType === 'clinic';

    const fetchAll = useCallback(async () => {
        try {
            const snap = await getDoc(doc(db, 'testRequests', requestId));
            if (!snap.exists()) { setRequest(null); return; }
            setRequest({ id: snap.id, ...snap.data() });
            // 당사자만 read 가능 — 권한 없으면 조용히 무시
            try {
                const priv = await getDoc(doc(db, 'testRequests', requestId, 'private', 'info'));
                if (priv.exists()) setPrivateInfo(priv.data());
            } catch (_) { /* 비당사자 */ }
        } catch (e) {
            console.error('상세 조회 실패:', e);
        } finally {
            setLoading(false);
        }
    }, [requestId]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const callFn = (name) => httpsCallable(getFunctions(undefined, 'asia-northeast3'), name);

    const handleAccept = async () => {
        if (!uid) { navigate('/login'); return; }
        // 건당 이용료·매칭 수수료 전면 폐지 (2026-08-17 대표 확정, 약관 v6.0) — 수락·매칭 모두 무료
        const confirmMsg = request?.fromLead
            ? '이 케이스를 수락하시겠습니까?\n\n· 수락과 매칭에 비용은 없습니다 (이용료·수수료 폐지)\n· 수락 후 담당자(010-2972-3733)에게 전화 주시면 치과 정보를 안내해드립니다'
            : '이 케이스를 수락하시겠습니까?\n\n· 수락에 비용은 없습니다 (이용료·수수료 폐지)';
        if (!window.confirm(confirmMsg)) return;
        setWorking(true);
        try {
            const res = await callFn('acceptTestRequest')({ requestId });
            alert(res.data.leadCase
                ? '수락되었습니다!\n담당자(010-2972-3733)에게 전화 주시면 치과 정보와 진행 방법을 안내해드립니다.'
                : '수락되었습니다! 치과 정보가 공개되었습니다.');
            await fetchAll();
        } catch (err) {
            const msg = err.message || '';
            if (msg.includes('NEED_PASS')) {
                setShowPassModal(true);
            } else if (msg.includes('NEED_BILLING_KEY')) {
                // 수수료 폐지(2026-08-17) 후 이 분기는 원칙적으로 안 온다 — 방어적 안내만 유지
                if (window.confirm('결제 카드 등록이 필요합니다.\n카드 등록 화면으로 이동할까요?')) {
                    navigate('/subscription');
                }
            } else {
                alert(msg || '수락에 실패했습니다.');
            }
        } finally {
            setWorking(false);
        }
    };

    const handlePurchasePass = async () => {
        setWorking(true);
        try {
            await callFn('purchaseTestPass')();
            setShowPassModal(false);
            alert('이용권 결제가 완료되었습니다. 이어서 수락을 진행합니다.');
            const res = await callFn('acceptTestRequest')({ requestId });
            alert('수락되었습니다! 치과 정보가 공개되었습니다.');
            void res;
            await fetchAll();
        } catch (err) {
            const msg = err.message || '';
            if (msg.includes('NEED_BILLING_KEY')) {
                if (window.confirm('등록된 결제 카드가 없습니다. 카드 등록 화면으로 이동할까요?')) {
                    navigate('/subscription');
                }
            } else {
                alert(msg || '이용권 구매에 실패했습니다.');
            }
        } finally {
            setWorking(false);
        }
    };

    const handleStatus = async (status, confirmMsg) => {
        if (!window.confirm(confirmMsg)) return;
        setWorking(true);
        try {
            await callFn('updateTestRequestStatus')({ requestId, status });
            await fetchAll();
        } catch (err) {
            alert(err.message || '상태 변경에 실패했습니다.');
        } finally {
            setWorking(false);
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: 80, color: '#9CA3AF' }}>불러오는 중...</div>;
    if (!request) return <div style={{ textAlign: 'center', padding: 80, color: '#9CA3AF' }}>의뢰서를 찾을 수 없습니다.</div>;

    // 이미 연결된 거래처의 게시글은 열람 차단 (당사자 제외)
    const myIds = [uid, user?.companyId].filter(Boolean);
    const isExcluded = (request.excludedLabIds || []).some(id => myIds.includes(id));
    if (isExcluded && !privateInfo) {
        return (
            <div style={{ textAlign: 'center', padding: 80, color: '#9CA3AF' }}>
                이미 연결된 거래처의 의뢰는 열람할 수 없습니다.<br />
                <span style={{ fontSize: 13 }}>테스트보철은 새로운 거래처를 만나기 위한 기능입니다.</span>
            </div>
        );
    }

    const s = STATUS_LABEL[request.status] || STATUS_LABEL.open;
    const c = request.caseInfo || {};
    // 회사 ID 통일(2026-07-17) 반영 — 개인 uid + 소속 회사 ID 모두 당사자로 인정
    const myPartyIds = [uid, user?.companyId].filter(Boolean);
    const isClinicParty = privateInfo && myPartyIds.includes(privateInfo.clinicId);
    const isLabParty = privateInfo && myPartyIds.includes(privateInfo.acceptedLabId);

    const InfoRow = ({ label, value }) => value ? (
        <div style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #F3F4F6', fontSize: 14 }}>
            <span style={{ width: 110, color: '#6B7280', flexShrink: 0 }}>{label}</span>
            <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
        </div>
    ) : null;

    return (
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px' }}>
            <button onClick={() => navigate('/test-cases')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 14, marginBottom: 16, padding: 0 }}>
                <ArrowLeft size={16} /> {isDental ? 'A/S 보철 의뢰 목록' : '테스트보철 게시판'}
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{request.testNumber}</h1>
                <span style={{ fontSize: 13, fontWeight: 700, color: s.color, background: s.bg, padding: '6px 14px', borderRadius: 14 }}>{s.text}</span>
            </div>

            {/* 공개 케이스 정보 */}
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, padding: '8px 20px', marginBottom: 16, background: '#fff' }}>
                {(isClinicParty || isLabParty) && (
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#6B7280', padding: '10px 0 2px' }}>
                        공개 정보 (게시판에 노출되는 요약)
                    </div>
                )}
                <InfoRow label="보철 종류" value={c.prosthesisType} />
                <InfoRow label="치식" value={c.teeth?.join(', ')} />
                <InfoRow label="재료" value={c.material} />
                <InfoRow label="쉐이드" value={c.shade} />
                <InfoRow label="희망 납기" value={c.dueDate} />
                <InfoRow label="지역" value={request.region} />
                <InfoRow label="특이사항" value={c.notes} />
            </div>

            {/* 당사자 전용: 의뢰서 전체 (수락 후 기공소 / 작성 치과) */}
            {(isClinicParty || isLabParty) && privateInfo?.orderDetail && (
                <div style={{ border: '1px solid #DDD6FE', background: '#F5F3FF', borderRadius: 12, padding: '8px 20px 14px', marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED', padding: '10px 0 6px' }}>
                        의뢰서 상세 (비공개 — {isLabParty ? '수락 기공소에게만 공개' : '나와 수락 기공소만 열람'})
                    </div>
                    {(privateInfo.orderDetail.prosthesisGroups || []).map((g, i) => (
                        <div key={`p${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '8px 0', borderBottom: '1px solid #EDE9FE' }}>
                            <span style={{ fontWeight: 600 }}>{g.prosthesis} <span style={{ color: '#6B7280', fontWeight: 400 }}>({g.material})</span></span>
                            <span style={{ color: '#374151' }}>치식 {g.teeth?.join(', ')}</span>
                        </div>
                    ))}
                    {(privateInfo.orderDetail.implantGroups || []).map((g, i) => (
                        <div key={`i${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '8px 0', borderBottom: '1px solid #EDE9FE' }}>
                            <span style={{ fontWeight: 600, color: '#2563EB' }}>임플란트 <span style={{ color: '#6B7280', fontWeight: 400 }}>{g.brand}</span></span>
                            <span style={{ color: '#374151' }}>치식 {g.teeth?.join(', ')}</span>
                        </div>
                    ))}
                    {(privateInfo.orderDetail.bridgeGroups || []).map((g, i) => (
                        <div key={`b${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '8px 0', borderBottom: '1px solid #EDE9FE' }}>
                            <span style={{ fontWeight: 600, color: '#7C3AED' }}>브릿지</span>
                            <span style={{ color: '#374151' }}>치식 {g.teeth?.join(' - ')}</span>
                        </div>
                    ))}
                    {(privateInfo.orderDetail.ponticGroups || []).map((g, i) => (
                        <div key={`po${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '8px 0', borderBottom: '1px solid #EDE9FE' }}>
                            <span style={{ fontWeight: 600, color: '#D97706' }}>폰틱</span>
                            <span style={{ color: '#374151' }}>치식 {g.teeth?.join(', ')}</span>
                        </div>
                    ))}
                    {privateInfo.orderDetail.shade && (
                        <div style={{ fontSize: 13, color: '#374151', paddingTop: 8 }}>쉐이드: <b>{privateInfo.orderDetail.shade}</b></div>
                    )}
                    {(privateInfo.orderDetail.specialNotes || privateInfo.orderDetail.notes) && (
                        <div style={{ fontSize: 13, color: '#374151', paddingTop: 4 }}>특이사항: {privateInfo.orderDetail.specialNotes || privateInfo.orderDetail.notes}</div>
                    )}
                    {/* 첨부 이미지 */}
                    {(privateInfo.attachments || []).length > 0 && (
                        <div style={{ paddingTop: 10 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>첨부 이미지</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {privateInfo.attachments.map((url, i) => (
                                    <a key={i} href={url} target="_blank" rel="noreferrer">
                                        <img src={url} alt={`첨부 ${i + 1}`} style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E7EB' }} />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* 디지털 파일 */}
                    {(privateInfo.orderDetail.digitalFiles || []).length > 0 && (
                        <div style={{ paddingTop: 10 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>디지털 파일</div>
                            {privateInfo.orderDetail.digitalFiles.map((f, i) => (
                                <a key={i} href={f.url || f.downloadURL || '#'} target="_blank" rel="noreferrer"
                                    style={{ display: 'block', fontSize: 13, color: '#2563EB', padding: '3px 0', textDecoration: 'underline' }}>
                                    {f.name || f.fileName || `파일 ${i + 1}`}
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* 당사자 전용: 상대방 정보 (매칭 후) */}
            {(isClinicParty || isLabParty) && (
                <div style={{ border: '1px solid #BFDBFE', background: '#EFF6FF', borderRadius: 12, padding: '8px 20px', marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#2563EB', padding: '10px 0 2px' }}>
                        {isClinicParty ? '내 의뢰 (비공개 정보)' : '치과 정보 (매칭 공개)'}
                    </div>
                    {isLabParty && privateInfo.hideClinicContact ? (
                        <>
                            <InfoRow label="치과명" value={privateInfo.clinicName} />
                            <div style={{ background: '#eef1f8', border: '1px solid #d8e0f0', borderRadius: 10, padding: '12px 14px', margin: '10px 0', fontSize: 13.5, color: '#1d3568', lineHeight: 1.7 }}>
                                케이스 진행을 위해 <b>담당자 010-2972-3733</b>으로 전화 주세요.<br />
                                치과 연락처와 상세 내용을 안내해드립니다.
                                <div style={{ marginTop: 8 }}>
                                    <a href="tel:01029723733" style={{ display: 'inline-block', background: '#274690', color: '#fff', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>📞 담당자에게 전화</a>
                                </div>
                            </div>
                        </>
                    ) : isLabParty && (
                        <>
                            <InfoRow label="치과명" value={privateInfo.clinicName} />
                            <InfoRow label="연락처" value={privateInfo.clinicPhone} />
                            <InfoRow label="주소" value={privateInfo.clinicAddress} />
                        </>
                    )}
                    {isClinicParty && (
                        <>
                            <InfoRow label="환자 식별" value={privateInfo.patientRef} />
                            {privateInfo.acceptedLabId && (
                                <>
                                    <InfoRow label="수락 기공소" value={privateInfo.labName} />
                                    <InfoRow label="기공소 연락처" value={privateInfo.labPhone} />
                                    <InfoRow label="기공소 주소" value={privateInfo.labAddress} />
                                </>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* 액션 버튼 */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {isLab && request.status === 'open' && !isClinicParty && (
                    <button onClick={handleAccept} disabled={working} style={btnStyle('#059669')}>
                        <CheckCircle2 size={16} /> {working ? '처리 중...' : '이 케이스 수락하기 (선착순)'}
                    </button>
                )}
                {isLabParty && request.status === 'accepted' && (
                    <button onClick={() => handleStatus('in_progress', '제작을 시작하시겠습니까?')} disabled={working} style={btnStyle('#D97706')}>제작 시작</button>
                )}
                {isLabParty && ['accepted', 'in_progress'].includes(request.status) && (
                    <button onClick={() => handleStatus('delivered', '납품 완료로 변경하시겠습니까?')} disabled={working} style={btnStyle('#7C3AED')}>납품 완료</button>
                )}
                {isClinicParty && request.status === 'delivered' && (
                    <button onClick={() => handleStatus('completed', '테스트보철 수령을 확인하고 완료 처리하시겠습니까?')} disabled={working} style={btnStyle('#2563EB')}>완료 확정</button>
                )}
                {isClinicParty && request.status === 'open' && (
                    <button onClick={() => handleStatus('cancelled', '의뢰를 취소하시겠습니까?')} disabled={working} style={btnStyle('#DC2626')}>의뢰 취소</button>
                )}
                {!user && request.status === 'open' && (
                    <button onClick={() => navigate('/login')} style={btnStyle('#2563EB')}>로그인하고 수락하기</button>
                )}
            </div>

            {/* 이용권 구매 모달 */}
            {showPassModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 380, width: '90%' }}>
                        <h3 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CreditCard size={20} color="#2563EB" /> 테스트보철 이용권 구매
                        </h3>
                        <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.7, margin: '0 0 20px' }}>
                            이번 달 무료 수락 1건을 이미 사용하셨습니다.<br />
                            이용권 <b style={{ color: '#111827' }}>10,000원 (VAT 포함)</b>을 구매하면 이 케이스를 수락할 수 있습니다.
                            (이용권으로 한 달 최대 100건까지 수락 가능)<br />
                            등록된 카드로 즉시 결제됩니다.
                        </p>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={handlePurchasePass} disabled={working} style={{ ...btnStyle('#2563EB'), flex: 1, justifyContent: 'center' }}>
                                {working ? '결제 중...' : '10,000원 결제 후 수락'}
                            </button>
                            <button onClick={() => setShowPassModal(false)} disabled={working} style={{ flex: 0, background: '#F3F4F6', color: '#374151', border: 'none', padding: '12px 16px', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                                취소
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TestCaseDetail;
